import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import tagsApi from 'api/requests/tags';
import { useTags } from 'context/events/Tags';
import { AlertCircle, ArrowLeft, ArrowRight, Check, Tag } from 'lucide-react';

export default function SignUpTagsSelect({ onNext, onBack, isLoading: externalLoading, error: externalError }) {
  const { tags, setTags } = useTags();
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [loadCategories, setLoadCategories] = useState(false);
  const [tagsError, setTagsError] = useState(false);

  useEffect(() => {
    getCategories();
  }, []);

  async function getCategories() {
    if (tags) return;

    setLoadCategories(true);
    setTagsError(false);

    try {
      const { data } = await tagsApi.get();
      setTags(data);
    } catch (error) {
      setTagsError(true);
    } finally {
      setLoadCategories(false);
    }
  }

  function toggleTag(tagId) {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }

  function handleSubmit() {
    if (selectedTagIds.length === 0) return;
    onNext(selectedTagIds);
  }

  return (
    <Container>
      <HeaderArea>
        <BackButton onClick={onBack} aria-label="Voltar">
          <ArrowLeft size={18} color={colors.night} />
        </BackButton>
        <StepIndicator>
          <StepDot />
          <StepDot active />
        </StepIndicator>
      </HeaderArea>

      <IconCircle>
        <Tag size={22} color={colors.occasionalPurple} />
      </IconCircle>
      <Title>Quase lá!</Title>
      <Subtitle>Selecione categorias do seu interesse</Subtitle>

      {(tagsError || externalError) && (
        <ErrorBanner>
          <AlertCircle size={16} color={colors.danger} />
          <Text color={colors.danger} name="small" type="bold">
            {externalError || 'Erro ao carregar categorias. Tente novamente.'}
          </Text>
        </ErrorBanner>
      )}

      <TagsContainer>
        {loadCategories ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <TagsGrid>
            {tags?.map((tag) => {
              const isSelected = selectedTagIds.includes(tag.id);
              return (
                <TagChip
                  key={tag.id}
                  selected={isSelected}
                  onClick={() => toggleTag(tag.id)}
                  type="button"
                >
                  {isSelected && <Check size={14} />}
                  <span>{tag.name}</span>
                </TagChip>
              );
            })}
          </TagsGrid>
        )}
      </TagsContainer>

      {selectedTagIds.length > 0 && (
        <SelectedCount>
          {selectedTagIds.length} {selectedTagIds.length === 1 ? 'categoria selecionada' : 'categorias selecionadas'}
        </SelectedCount>
      )}

      {externalLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <SubmitButton
          onClick={handleSubmit}
          disabled={selectedTagIds.length === 0}
          type="button"
        >
          <span>Criar minha conta</span>
          <ArrowRight size={18} />
        </SubmitButton>
      )}
    </Container>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StepDot = styled.div`
  width: ${({ active }) => (active ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${({ active }) => (active ? colors.occasionalPurple : colors.neutralDark)};
  transition: all 0.3s ease;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3eaff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  margin-bottom: 16px;
  text-align: center;
`;

const TagsContainer = styled.div`
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 2px;
  margin-bottom: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.nightLight};
    border-radius: 4px;
  }
`;

const TagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const TagChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid
    ${({ selected }) => (selected ? colors.occasionalPurple : colors.neutralDark)};
  background: ${({ selected }) => (selected ? '#f3eaff' : colors.neutralLight)};
  color: ${({ selected }) => (selected ? colors.occasionalPurple : colors.nightDark)};

  &:hover {
    border-color: ${colors.occasionalPurple}60;
    background: ${({ selected }) => (selected ? '#f3eaff' : '#fafaff')};
  }

  &:active {
    transform: scale(0.97);
  }
`;

const SelectedCount = styled.p`
  font-size: 12px;
  color: ${colors.occasionalPurple};
  font-weight: 600;
  margin-bottom: 8px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fef2f2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 4px auto 0;
  padding: 12px 28px;
  background: ${({ disabled }) => (disabled ? colors.nightLight : colors.secondaryPurple)};
  color: ${colors.neutralLight};
  border: 1px solid ${({ disabled }) => (disabled ? colors.nightLight : colors.secondaryPurple)};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  box-shadow: ${({ disabled }) =>
    disabled ? 'none' : `0 2px 3px ${colors.nightLight}`};

  &:hover:not(:disabled) {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(128, 37, 232, 0.25);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
  width: 100%;
`;
